/**
 * The empty-document creation CACHE ENGINE (ARCFE-1004).
 *
 * Owns every cache write in the optimistic create/commit/rollback cycle over
 * three frozen identities — the documents list, the placeholder document
 * detail, and the session row — plus the two pure rules the cycle depends on
 * (initial streaming status, generation-type mapping) and the failure
 * classification.
 *
 * DOMAIN RULING — the hook SHELL stays app-side. Its remaining work is the
 * session-window tab choreography (UI state, ruled out of the data claim at
 * ARCFE-1003: `commitDocumentTab`, `addDocumentTabIfMissing`,
 * `removeOptimisticDocumentTab` and the pending-focus intent), the dictation
 * and delay-upgrade atoms, `mapNewBrain`, the personalization settings read,
 * analytics, toasts and intl. The shell interleaves tab writes between these
 * cache writes, so it drives the sequence; the identities and the
 * reconciliation rules live here.
 */
import { DocumentGenType, GenerationMethod } from '@heidi/schema/ml-scribe';
import type { QueryClient } from '@tanstack/react-query';
import { GET_SESSION_BY_ID, sessionKeys } from '../../contracts/keys';
import { StreamingStatus } from '../../contracts/streamingStatus';

type DocumentRow = { id: string };
type DocumentsList<T extends DocumentRow> = { documents: T[]; total: number };

/** `[GET_DOCUMENT_BY_ID, { sessionId, documentId }]` — the detail identity. */
export const optimisticDocumentKey = (sessionId: string, documentId: string) =>
  sessionKeys.documentById(sessionId, documentId);

// ---------------------------------------------------------------------------
// Pure rules
// ---------------------------------------------------------------------------

/**
 * The streaming status a freshly created document starts in.
 *
 * Dictation and GPCCMP documents are NOT_STARTED — nothing streams into them
 * until the user acts. Everything else is AWAITING_STREAMING_START, which is
 * what makes the tab render its loading state instead of an empty note.
 *
 * `documentTabType` is compared against the literal `'DICTATION'` rather than
 * importing `DocumentTabType` so this rule stays usable from callers holding
 * either the enum or its string value.
 */
export const getInitialStreamingStatus = (
  documentTabType?: string,
  generationMethod?: GenerationMethod
): StreamingStatus => {
  if (documentTabType === 'DICTATION') {
    return StreamingStatus.NOT_STARTED;
  }

  if (generationMethod === GenerationMethod.GPCCMP) {
    return StreamingStatus.NOT_STARTED;
  }

  return StreamingStatus.AWAITING_STREAMING_START;
};

/**
 * The optimistic row's `generation_type`. Only four methods map to a distinct
 * type; everything else (including dictation and live dictation) is seeded as
 * INTERNAL_TEMPLATE — the pre-move behaviour, preserved rather than corrected,
 * because the server response overwrites this field on commit.
 */
export const mapGenerationMethodToGenType = (
  generationMethod: GenerationMethod
): DocumentGenType => {
  switch (generationMethod) {
    case GenerationMethod.ASK_AI:
      return DocumentGenType.ASK_AI;
    case GenerationMethod.PRESET_AI_COMMAND:
      return DocumentGenType.PRESET_AI_COMMAND;
    case GenerationMethod.TEMPLATE:
      return DocumentGenType.TEMPLATE;
    case GenerationMethod.GPCCMP:
      return DocumentGenType.GPCCMP;
    default:
      return DocumentGenType.INTERNAL_TEMPLATE;
  }
};

/** The optimistic document's display name, in the pre-move precedence. */
export const resolveOptimisticDocumentName = ({
  name,
  aiCommandText,
}: {
  name?: string;
  aiCommandText?: string;
}): string => name || aiCommandText || 'Untitled Document';

// ---------------------------------------------------------------------------
// Optimistic seed
// ---------------------------------------------------------------------------

/**
 * Seed the placeholder into both the detail cache and the list.
 *
 * Returns the snapshot the rollback needs. The list snapshot is captured for
 * the empty-cache case only — the rollback is surgical (it removes the
 * placeholder by id) precisely so it cannot clobber writes that landed while
 * the request was in flight.
 */
export const seedOptimisticDocument = async <T extends DocumentRow, D>(
  queryClient: QueryClient,
  {
    sessionId,
    placeholderId,
    seed,
    optimisticRow,
  }: {
    sessionId: string;
    placeholderId: string;
    /** The full placeholder detail row to write (app-owned `mockEmptyDocument`). */
    seed: D;
    /** The placeholder row to append to the list. */
    optimisticRow: T;
  }
): Promise<{
  documents: DocumentsList<T> | undefined;
  previousEmptyDocument: D | undefined;
}> => {
  const documentsKey = sessionKeys.documents(sessionId);
  const detailKey = optimisticDocumentKey(sessionId, placeholderId);

  await Promise.all([
    queryClient.cancelQueries({ queryKey: documentsKey, exact: true }),
    queryClient.cancelQueries({ queryKey: detailKey, exact: true }),
  ]);

  const documents = queryClient.getQueryData<DocumentsList<T>>(documentsKey);
  const previousEmptyDocument = queryClient.getQueryData<D>(detailKey);

  queryClient.setQueryData(detailKey, () => seed);

  queryClient.setQueryData<DocumentsList<T>>(documentsKey, {
    documents: [...(documents?.documents || []), optimisticRow],
    total: (documents?.total || 0) + 1,
  });

  return { documents, previousEmptyDocument };
};

// ---------------------------------------------------------------------------
// Commit
// ---------------------------------------------------------------------------

/**
 * Swap the placeholder for the persisted document in the list.
 *
 * Runs BEFORE the tab is activated: reconciliation rejects active ids missing
 * from this cache, which would bounce a successful creation back to the Note
 * tab. Filtering on the real id too clears a placeholder left behind by a
 * quota-rejected attempt.
 */
export const commitCreatedDocumentToList = <T extends DocumentRow>(
  queryClient: QueryClient,
  {
    sessionId,
    placeholderId,
    document,
  }: { sessionId: string; placeholderId: string; document: T }
): void => {
  queryClient.setQueryData<DocumentsList<T>>(sessionKeys.documents(sessionId), (old) => {
    if (!old) {
      return { documents: [document], total: 1 };
    }

    const filteredDocs = old.documents.filter(
      (doc) => doc.id !== placeholderId && doc.id !== document.id
    );
    const newDocuments = [...filteredDocs, document];

    return { ...old, documents: newDocuments, total: newDocuments.length };
  });
};

/** Drop the placeholder detail entry once the real document exists. */
export const removeOptimisticDocumentDetail = (
  queryClient: QueryClient,
  { sessionId, placeholderId }: { sessionId: string; placeholderId: string }
): void => {
  queryClient.removeQueries({
    queryKey: optimisticDocumentKey(sessionId, placeholderId),
    exact: true,
  });
};

/**
 * Warm the created document's detail cache so its tab renders without a fetch.
 *
 * Deliberately non-throwing: the creation is already committed, and a throw
 * here would route into `onError` and roll back a document that exists on the
 * backend. The tab's own query retries the fetch on mount.
 */
export const prefetchCreatedDocumentDetail = async <T>(
  queryClient: QueryClient,
  {
    sessionId,
    documentId,
    fetchDocument,
    streamingStatus,
    onPrefetchFailed,
  }: {
    sessionId: string;
    documentId: string;
    fetchDocument: (sessionId: string, documentId: string) => Promise<{ data: T }>;
    streamingStatus: StreamingStatus;
    onPrefetchFailed?: (error: unknown) => void;
  }
): Promise<void> => {
  try {
    await queryClient.ensureQueryData({
      // `streamingStatus` is a plain argument seeded into the cached VALUE, not
      // part of the identity — adding it to the key would fork the document's
      // cache entry per streaming state. The pre-move code passed the same key
      // as an inline literal, which the rule could read; a key factory hides
      // the deps from it.
      // eslint-disable-next-line @tanstack/query/exhaustive-deps
      queryKey: sessionKeys.documentById(sessionId, documentId),
      queryFn: async () => {
        const { data } = await fetchDocument(sessionId, documentId);
        return { ...data, streamingStatus };
      },
    });
  } catch (error) {
    onPrefetchFailed?.(error);
  }
};

// ---------------------------------------------------------------------------
// Rollback
// ---------------------------------------------------------------------------

/**
 * Undo the optimistic seed.
 *
 * Surgical by design: drops only the placeholder rather than restoring the
 * whole-list snapshot, which would clobber writes that landed while this
 * request was in flight. The trailing invalidate restores truth — and covers
 * the case where `seedOptimisticDocument` cancelled the initial fetch before it
 * ever resolved.
 */
export const rollbackOptimisticDocument = <T extends DocumentRow, D>(
  queryClient: QueryClient,
  {
    sessionId,
    placeholderId,
    snapshot,
    previousEmptyDocument,
  }: {
    sessionId: string;
    placeholderId: string;
    snapshot: DocumentsList<T> | undefined;
    previousEmptyDocument: D | undefined;
  }
): void => {
  const documentsKey = sessionKeys.documents(sessionId);
  const detailKey = optimisticDocumentKey(sessionId, placeholderId);

  queryClient.setQueryData<DocumentsList<T>>(documentsKey, (current) => {
    if (!current) {
      return snapshot;
    }

    const documents = current.documents.filter((doc) => doc.id !== placeholderId);
    return { ...current, documents, total: documents.length };
  });

  if (previousEmptyDocument) {
    queryClient.setQueryData(detailKey, previousEmptyDocument);
  } else {
    queryClient.removeQueries({ queryKey: detailKey, exact: true });
  }

  void queryClient.invalidateQueries({ queryKey: documentsKey, exact: true });
};

// ---------------------------------------------------------------------------
// Failure classification
// ---------------------------------------------------------------------------

export type CreateEmptyDocumentFailure =
  | { kind: 'unlimited-quota-until-unset' }
  | { kind: 'quota-exceeded' }
  | { kind: 'request-failed'; message: string | undefined }
  | { kind: 'unknown' };

/**
 * Classify a create failure from the error payload.
 *
 * Handles both the nested (`detail.type`) and flat (`type`) response shapes —
 * the backend emits both, and reading only one silently degraded every quota
 * rejection to a generic toast.
 *
 * `isAxiosError` is injected: axios identifies its errors through a marker its
 * own instance sets, so the app passes the predicate bound to the same axios
 * the request was made with.
 */
export const classifyCreateEmptyDocumentFailure = (
  error: unknown,
  isAxiosError: (error: unknown) => boolean
): CreateEmptyDocumentFailure => {
  if (!isAxiosError(error)) {
    return { kind: 'unknown' };
  }

  const errorData = (error as { response?: { data?: unknown } }).response?.data as
    | { detail?: { type?: string; msg?: string }; type?: string; msg?: string }
    | undefined;
  const errorType = errorData?.detail?.type ?? errorData?.type;

  if (errorType === 'UnlimitedQuotaUntilNotSetError') {
    return { kind: 'unlimited-quota-until-unset' };
  }

  if (errorType === 'ASKAIQuotaExceedError' || errorType === 'TemplateQuotaExceedError') {
    return { kind: 'quota-exceeded' };
  }

  return {
    kind: 'request-failed',
    message:
      errorData?.detail?.msg ?? errorData?.msg ?? (error as { message?: string }).message,
  };
};

/**
 * Clear the session's streaming state after a quota rejection, so the UI does
 * not sit in a permanent "generating" state behind the upgrade modal. The fresh
 * `AbortController` replaces the one the aborted attempt consumed.
 */
export const resetSessionStreamingAfterQuotaError = (
  queryClient: QueryClient,
  sessionId: string
): void => {
  queryClient.setQueryData<{
    abortController?: AbortController;
    streamingStatus?: StreamingStatus;
  }>([GET_SESSION_BY_ID, { sessionId }], (oldData) => {
    if (!oldData) {
      return undefined;
    }
    return {
      ...oldData,
      abortController: new AbortController(),
      streamingStatus: StreamingStatus.NOT_STARTED,
    };
  });
};
